<template>
  <div class="finance-dashboard" :class="{ 'finance-dashboard--dark': isDarkSkin }">

    <div class="finance-dashboard__toolbar">
      <filter-card :filter-key="viewKey" dashboard single-row v-model="filter" v-bind:fields="filterFields"/>
      <calendar-bar v-model="dateRange" @input="scheduleRefresh"/>
    </div>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <b-row class="kpi-row">
        <b-col xl="4" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--pending">
            <div class="kpi-card__icon"><feather-icon icon="ClockIcon" size="24"/></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('finance.pendingReview') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(stats.pendingAmount) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>{{ $t('Count') }}: {{ stats.pendingCount }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="4" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--success">
            <div class="kpi-card__icon"><feather-icon icon="CheckCircleIcon" size="24"/></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('finance.received') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(stats.approvedAmount) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>{{ $t('Count') }}: {{ stats.approvedCount }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>

        <b-col xl="4" md="6" sm="12" class="mb-2">
          <div class="kpi-card kpi-card--danger">
            <div class="kpi-card__icon"><feather-icon icon="XCircleIcon" size="24"/></div>
            <div class="kpi-card__body">
              <div class="kpi-card__title">{{ $t('finance.canceled') }}</div>
              <div class="kpi-card__value">{{ convertToEURO(stats.canceledAmount) }}</div>
              <div class="kpi-card__footer">
                <feather-icon icon="HashIcon" size="12" class="mr-25"/>{{ $t('Count') }}: {{ stats.canceledCount }}
              </div>
            </div>
            <div class="kpi-card__glow"></div>
          </div>
        </b-col>
      </b-row>

      <b-card no-body class="mt-1">
        <b-card-header class="pb-50 d-flex align-items-center justify-content-between flex-wrap">
          <h5 class="mb-0">{{ $t('finance.approvedDeposits') }}</h5>
          <b-button-group size="sm" class="fd-seg">
            <b-button v-for="s in segments" :key="s.value"
                      :variant="segment === s.value ? s.variant : 'outline-' + s.variant"
                      @click="segment = s.value">{{ s.label }}</b-button>
          </b-button-group>
        </b-card-header>
        <b-card-body>
          <monetaries-table :filters="financeFilters" :columns="financeColumns" :finance-mode="true"
                            trans-type="finance" :has-selection="false" :lazy="false"
                            @finance-updated="onFinanceUpdated"/>
        </b-card-body>
      </b-card>

    </b-overlay>
  </div>
</template>

<script>
import CalendarBar from '@/components/widget/CalendarBar';
import mixinBase from '@/components/mixin/mixinBase';
import { mapActions, mapGetters } from 'vuex';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import FilterCard from '@/components/widget/FilterCard';
import MonetariesTable from '@/components/tables/MonetariesTable';
import MonetaryDashboardSearchRequest from '@/model/Dashboard/MonetaryDashboardSearchRequest';
import MonetaryDashboardFilterFields from '@/model/filter/MonetaryDashboardFilterFields';

const REFRESH_DEBOUNCE_MS = 250;

export default {
  name: 'FinanceDashboard',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  components: { CalendarBar, FilterCard, MonetariesTable },
  data: () => ({
    filter: MonetaryDashboardSearchRequest(),
    filterFields: [],
    dateRange: { from: null, until: null },
    loading: false,
    inflight: false,
    pendingFetch: false,
    refreshTimer: null,
    segment: 'PENDING',
    stats: {
      pendingCount: 0, pendingAmount: 0,
      approvedCount: 0, approvedAmount: 0,
      canceledCount: 0, canceledAmount: 0,
    },
  }),
  watch: {
    filter: { handler: 'scheduleRefresh', deep: true },
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    viewKey() { return this.$route.name || 'finance-dashboard'; },
    segments() {
      return [
        { value: 'PENDING', label: this.$t('financeStatuses.PENDING'), variant: 'warning' },
        { value: 'APPROVED', label: this.$t('financeStatuses.APPROVED'), variant: 'success' },
        { value: 'CANCELED', label: this.$t('financeStatuses.CANCELED'), variant: 'danger' },
        { value: 'ALL', label: this.$t('finance.all'), variant: 'primary' },
      ];
    },
    financeColumns() {
      return [
        { key: 'clientDetails.accountId', enable: true },
        { key: 'clientDetails.email', enable: true },
        { key: 'amount', enable: true },
        { key: 'currency', enable: true },
        { key: 'paymentDetails.processorName', enable: true },
        { key: 'registrationTime', enable: true },
        { key: 'status', enable: true },
      ];
    },
    financeFilters() {
      return {
        type: 'DEPOSIT',
        orgIds: this.filter.orgIds,
        businessUnit: this.filter.buIds,
        registrationTime: {
          start: this.$moment(this.dateRange.from).toServerDateTimeFormat(),
          end: this.$moment(this.dateRange.until).toServerDateTimeFormat(),
        },
        realType: this.filter.realOnly === false ? null : ['REAL'],
        status: ['APPROVED', 'CANCELED'],
        action: ['CREDIT_LINE'],
        isTest: false,
        financeStatus: this.segment === 'ALL' ? null : [this.segment],
      };
    },
  },
  created() {
    this.filterFields = MonetaryDashboardFilterFields();
    this.dateRange = {
      from: this.$moment().startOf('month').format('YYYY-MM-DD'),
      until: this.$moment().endOf('month').format('YYYY-MM-DD'),
    };
  },
  mounted() { this.$root.$on('dashboard::refresh', this.scheduleRefresh); },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = null; }
  },
  methods: {
    ...mapActions('dashboard', ['getFinanceDashboard']),
    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (!this.dateRange.from) return;
      if (this.inflight) { this.pendingFetch = true; return; }
      this.doFetch();
    },
    doFetch() {
      const payload = { ...this.filter, range: { from: this.dateRange.from, until: this.dateRange.until } };
      this.inflight = true;
      this.loading = true;
      this.getFinanceDashboard(payload)
        .then(
          data => this.setData(data),
          err => this.showServerErrorNotification('getFinanceDashboard', 'financeDashboard', err)
        )
        .finally(() => {
          this.inflight = false;
          this.loading = false;
          if (this.pendingFetch) { this.pendingFetch = false; this.doFetch(); }
        });
    },
    setData(data) {
      if (!data) return;
      this.stats.pendingCount = data.pendingCount || 0;
      this.stats.pendingAmount = data.pendingAmount || 0;
      this.stats.approvedCount = data.approvedCount || 0;
      this.stats.approvedAmount = data.approvedAmount || 0;
      this.stats.canceledCount = data.canceledCount || 0;
      this.stats.canceledAmount = data.canceledAmount || 0;
    },
    onFinanceUpdated() {
      // A finance action changed a transaction — refresh the KPI cards.
      this.scheduleRefresh();
    },
  },
};
</script>

<style scoped lang="scss">
.finance-dashboard {
  position: relative;
  &__toolbar { display: flex; flex-direction: column; gap: 0.5rem; }
}
.kpi-row { margin-top: 0.5rem; }
.fd-seg { margin-top: 0.25rem; }

.kpi-card {
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.35rem;
  border-radius: 18px;
  color: #fff;
  overflow: hidden;
  box-shadow: 0 12px 28px -14px rgba(20, 24, 50, 0.28), 0 2px 4px rgba(20, 24, 50, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  min-height: 118px;

  &:hover { transform: translateY(-3px); box-shadow: 0 18px 40px -16px rgba(20, 24, 50, 0.35), 0 4px 8px rgba(20, 24, 50, 0.08); }

  &__icon {
    position: relative; z-index: 2;
    display: flex; align-items: center; justify-content: center;
    width: 52px; height: 52px; border-radius: 14px;
    background: rgba(255, 255, 255, 0.22); backdrop-filter: blur(6px); flex-shrink: 0;
  }
  &__body { position: relative; z-index: 2; flex: 1; min-width: 0; }
  &__title {
    font-size: 0.82rem; font-weight: 500; text-transform: uppercase; letter-spacing: 0.04em;
    opacity: 0.86; margin-bottom: 0.25rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  &__value { font-size: 1.55rem; font-weight: 700; line-height: 1.15; letter-spacing: -0.01em; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__footer { margin-top: 0.45rem; font-size: 0.76rem; opacity: 0.9; display: flex; align-items: center; }
  &__glow { position: absolute; top: -40%; right: -20%; width: 220px; height: 220px; border-radius: 50%; background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%); pointer-events: none; z-index: 1; }

  &--success { background: linear-gradient(135deg, #34d399 0%, #10b981 55%, #059669 100%); }
  &--danger { background: linear-gradient(135deg, #fb7185 0%, #ef4444 55%, #c1272d 100%); }
  &--pending { background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 55%, #d97706 100%); }
}

@media (max-width: 576px) {
  .kpi-card { min-height: auto; padding: 1rem; &__value { font-size: 1.3rem; } }
}
</style>
